/**
 * @fileoverview File validation utilities for upload security.
 *
 * Provides comprehensive file validation including:
 * - File extension whitelisting
 * - File size limits (10MB default)
 * - Filename security checks
 *
 * @module helpers/validateFiles
 */

import _ from "lodash";

/**
 * Validates multiple files against extension and size rules.
 *
 * @param {Array<{name: string, size: number}>} files - Array of file objects
 * @param {string} [allowExtensionString] - Comma-separated allowed extensions
 * @param {number} [sizeLimitKB] - Maximum allowed file size in KB (default 10240 = 10MB)
 * @returns {{isValid: boolean, errors: string[]}} Validation result
 *
 * @example
 * const { isValid, errors } = validateFiles(fileList, '.pdf,.docx', 35840);
 * if (!isValid) {
 *   errors.forEach(err => console.error(err));
 * }
 */
export function validateFiles(
  files: { name: string; size: number }[],
  allowExtensionString: string | undefined,
  sizeLimitKB?: number
): { isValid: boolean; errors: string[] } {
  let overallErrors: string[] = [];
  const validation: boolean[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const { isValid, errors } = validateFile(file, allowExtensionString, sizeLimitKB);
    if (errors.length > 0) {
      overallErrors = overallErrors.concat(errors);
    }
    validation.push(isValid);
  }

  const isValid = _.every(validation, (v) => !!v);

  return { isValid, errors: overallErrors };
}

/**
 * Validates a single file against extension and size rules.
 *
 * @param {Object} file - File object to validate
 * @param {string} file.name - Filename with extension
 * @param {number} file.size - File size in bytes
 * @param {string} [allowExtensionString] - Comma-separated allowed extensions
 * @param {number} [sizeLimitKB] - Maximum allowed file size in KB (default 10240 = 10MB)
 * @returns {{isValid: boolean, errors: string[]}} Validation result with error messages
 *
 * @example
 * const { isValid, errors } = validateFile(
 *   { name: 'document.pdf', size: 1024 },
 *   '.pdf,.doc',
 *   35840
 * );
 */
export function validateFile(
  file: {
    name: string;
    size: number;
  },
  allowExtensionString: string | undefined,
  sizeLimitKB?: number
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  const fileName = file.name;

  const whitelistedExtensionOnly = hasWhitelistedExtensions(fileName, allowExtensionString);

  if (!whitelistedExtensionOnly) {
    errors.push(
      `Invalid Action: '${fileName}' can't be uploaded as the file type is not supported by the system.`
    );
  }

  const validFileSize = hasValidFileSize(file, sizeLimitKB);
  if (!validFileSize) {
    const limitMB = sizeLimitKB !== undefined ? Math.round(sizeLimitKB / 1024) : 10;
    errors.push(`Invalid Action: file size greater than ${limitMB}mb`);
  }

  const isValid = whitelistedExtensionOnly && validFileSize;
  return { isValid, errors };
}

/**
 * Checks if a filename has a whitelisted extension.
 *
 * Default allowed extensions: .xls, .xlsx, .doc, .docx, .dotx, .jpeg,
 * .jpg, .png, .pdf, .svg, .gif, .json, .jfif
 *
 * The string "image/*" is expanded to common image formats.
 *
 * @param {string} fileName - Filename to check
 * @param {string} [allowExtensionString] - Comma-separated extensions (e.g., ".pdf,.doc")
 * @returns {boolean} True if extension is allowed
 *
 * @example
 * hasWhitelistedExtensions('doc.pdf', '.pdf,.doc'); // true
 * hasWhitelistedExtensions('script.exe', '.pdf,.doc'); // false
 * hasWhitelistedExtensions('photo.jpg', 'image/*'); // true
 */
export function hasWhitelistedExtensions(fileName: string, allowExtensionString: string | undefined): boolean {
  const defaultAllowedFileExtensions = [
    ".xls",
    ".xlsx",
    ".doc",
    ".docx",
    ".dotx",
    ".jpeg",
    ".jpg",
    ".png",
    ".pdf",
    ".svg",
    ".gif",
    ".json",
    ".jfif",
  ];
  const allowedFileExtensions = !allowExtensionString ? defaultAllowedFileExtensions : allowExtensionString.replace("image/*", ".jpg, .jpeg, .png, .gif, .bmp, .tiff, .tif, .svg, .webp, .ico, .heic, .heif").replace(/\s+/g, "").split(",");

  const fileExtRegex = new RegExp(
    "(" + allowedFileExtensions.join("|").replace(/\./g, "\\.") + ")$",
    "i"
  );
  const isAllowed = fileExtRegex.test(fileName);

  return isAllowed;
}

/**
 * Checks if a filename has only a single extension.
 *
 * Used to detect potentially malicious files with double extensions
 * (e.g., "document.pdf.exe").
 *
 * @param {string} fileName - Filename to check
 * @returns {boolean} True if file has exactly one extension
 *
 * @example
 * hasSingleExtension('document.pdf');     // true
 * hasSingleExtension('document.pdf.exe'); // false
 */
export function hasSingleExtension(fileName: string): boolean {
  const ext = fileName.split(".");
  const onlyOneExt = ext.length === 2;
  return onlyOneExt;
}

/**
 * Checks if a filename contains special characters.
 *
 * Special characters can cause issues with file systems or be used
 * in path traversal attacks.
 *
 * @param {string} fileName - Filename to check
 * @returns {boolean} True if filename has NO special characters (is valid)
 *
 * @example
 * noSpecialCharacter('document.pdf');  // true
 * noSpecialCharacter('doc<script>.pdf'); // false
 */
export function noSpecialCharacter(fileName: string): boolean {
  if (!fileName) {
    return true;
  }

  const regex = /[`~!@#$%^&*()|+=?;:'",<>\{\}\[\]\\\/]/gi;
  const haveSpecialCharacter = regex.test(fileName);

  return !haveSpecialCharacter;
}

/**
 * Checks if a file is within the size limit.
 *
 * @param {Object} file - File object
 * @param {number} file.size - File size in bytes
 * @param {number} [tenMbLimit=10240] - Maximum size in KB (default 10MB)
 * @returns {boolean} True if file size is within limit
 *
 * @example
 * hasValidFileSize({ size: 5000000 }); // true (5MB < 10MB)
 * hasValidFileSize({ size: 15000000 }); // false (15MB > 10MB)
 */
export function hasValidFileSize(file: { size: number }, tenMbLimit = 10240): boolean {
  const fileSize = file.size;
  const fileSizeInKB = Math.round(fileSize / 1024);

  const fileLessThan10Mb = fileSizeInKB <= tenMbLimit;
  return fileLessThan10Mb;
}
